import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SnapshotList from './SnapshotList';
import SnapshotsFilter from './SnapshotsFilter';
import SnapshotsSearchBar from './SnapshotsSearchBar';

const filterGroups = [
  {
    id: 'size',
    label: 'Size',
    options: [
      { id: 'small', label: 'Small' },
      { id: 'medium', label: 'Medium' },
      { id: 'large', label: 'Large' },
      { id: 'original', label: 'X-Large' }
    ],
    filterFn: (snapshot, selectedOptions) => {
      const selectedSizes = Object.keys(selectedOptions).filter((key) => selectedOptions[key]);

      if (selectedSizes.length === 0) return true;

      return selectedSizes.includes(snapshot?.rack_details?.gateway?.size);
    }
  },
  {
    id: 'baseline_type',
    label: 'Baseline Types',
    options: [{ id: 'pacos', label: 'PAC OS' }],
    filterFn: (snapshot, selectedOptions) => {
      if (!selectedOptions.pacos) return true;

      return snapshot?.name?.split('-')[0].includes('pk');
    }
  },
  {
    id: 'lrus',
    label: 'LRUs',
    options: [
      { id: 'is', label: 'X-IS' },
      { id: 'next_is', label: 'NEXT IS' },
      { id: 'ex3_nc', label: 'eX3 NC' },
      { id: 'next_nc', label: 'NEXT NC' },
      { id: 'cs4', label: 'CS4' },
      { id: 'next_cs4', label: 'NEXT CS4' }
    ],
    filterFn: (snapshot, selectedOptions) => {
      const selectedLrus = Object.keys(selectedOptions).filter((key) => selectedOptions[key]);

      if (selectedLrus.length === 0) return true;

      const snapshotLrus = snapshot?.rack_details?.lrus || {};
      // get the enabled LRUs from the snapshot
      const enabledLRUs = Object.keys(snapshotLrus).filter((lru) => snapshotLrus[lru].enabled);
      return selectedLrus.some((lru) => enabledLRUs.includes(lru));
    }
  }
];

SearchSnapshots.propTypes = {
  snapshotList: PropTypes.array,
  setCheckedSnapshot: PropTypes.func
};

export default function SearchSnapshots({ snapshotList, setCheckedSnapshot }) {
  const [filteredSnapshots, setFilteredSnapshots] = useState(snapshotList);
  const [searchString, setSearchString] = useState('');
  const [filters, setFilters] = useState({});

  // filters the snapshots with the search string provided
  const searchSnapshots = (snapshots) => {
    if (searchString !== '') {
      return snapshots.filter((snapshot) =>
        JSON.stringify(snapshot).toLowerCase().includes(searchString.toLowerCase())
      );
    }
    return snapshots;
  };

  // filters snapshots using the filter toggles that the user specified
  const filterSnapshots = (snapshots) => {
    const filtered = snapshots.filter((snapshot) => {
      const matchesTeamOnly = filters.global ? snapshot.global === false : true;

      const matchesGroups = filterGroups.every((group) => {
        const selectedOptions = filters[group.id] || {};

        if (!group.filterFn) return true;

        return group.filterFn(snapshot, selectedOptions);
      });

      return matchesTeamOnly && matchesGroups;
    });
    return filtered;
  };

  useEffect(() => {
    // update the filteredSnapshots if either the searchbar string, filters, or original list changes
    const filtered = searchSnapshots(snapshotList);
    setFilteredSnapshots(filterSnapshots(filtered));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotList, searchString, filters]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SnapshotsSearchBar searchString={searchString} setSearchString={setSearchString} />
      </Grid>
      <Grid item xs={12} sm={5} md={3}>
        <SnapshotsFilter filters={filters} setFilters={setFilters} filterGroups={filterGroups} />
      </Grid>
      <Grid item xs={12} sm={7} md={9}>
        <SnapshotList snapshotList={filteredSnapshots} setCheckedSnapshot={setCheckedSnapshot} />
      </Grid>
    </Grid>
  );
}
